import { useConfig } from '@/hooks/useConfig/useConfig';
import { useAirKeyTrace, useAirKeyTracePageView } from '@/utils/airKeyTrace';
import { cn } from '@/utils/cn';
import {
  Button,
  Column,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  Modal,
  TextBody,
  TextHeading,
} from '@cof/omni-react';

export const DenyVerificationModal = ({
  onDeny,
  onGoBack,
}: {
  onDeny: () => void;
  onGoBack: () => void;
}) => {
  useAirKeyTracePageView('deny_verification_modal');
  const sendTrace = useAirKeyTrace();
  const { locale } = useConfig();

  const translations: {
    [k: string]: {
      title: string;
      body: string;
      primary: string;
      secondary: string;
    };
  } = {
    'en-US': {
      title: "You're about to deny a verification request",
      body: "If you're sure you don't recognize this, deny this request.",
      primary: 'Deny this request',
      secondary: 'Go back',
    },
    'es-US': {
      title: 'Estás a punto de denegar una solicitud de verificación',
      body: 'Si estás seguro de que no reconoces esto, deniega esta solicitud.',
      primary: 'Denegar esta solicitud',
      secondary: 'Volver',
    },
  };

  const t = translations[locale] || translations['en-US'];

  const handleDeny = () => {
    sendTrace({
      view: 'deny_verification_modal',
      action: 'deny',
    });
    onDeny(); // Fires existing callback from templates
  };

  const handleGoBack = () => {
    sendTrace({
      view: 'deny_verification_modal',
      action: 'cancel',
    });
    onGoBack(); // Fires existing callback from templates
  };

  return (
    <Modal open={true}>
      <Dialog className={cn('bg-base1')}>
        <DialogHeader paddingBottom="zero">
          <TextHeading
            fontWeight="semibold"
            className={cn('text-neutral-primary')}
          >
            {t.title}
          </TextHeading>
        </DialogHeader>
        <DialogContent paddingBottom="tiny">
          <Column>
            <TextBody className={cn('text-neutral-primary')}>{t.body}</TextBody>
          </Column>
        </DialogContent>
        <DialogFooter padding="medium">
          <Column padding="small">
            <Button
              size="medium"
              className={cn(
                'text-neutral-inverse',
                'bg-critical-emphasis-high',
              )}
              rank="primary"
              onClick={handleDeny}
            >
              {t.primary}
            </Button>
            <Button
              size="medium"
              rank="tertiary"
              onClick={handleGoBack}
              className={cn('text-interactive')}
            >
              {t.secondary}
            </Button>
          </Column>
        </DialogFooter>
      </Dialog>
    </Modal>
  );
};
